/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, Link, SelectField, SelectOption, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon, ChevronNextIcon, ChevronPreviousIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockTabsQuery,
  EntWebsiteTabsFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import { entWebsiteBlockTabsQuery } from 'bundles/ent-website/queries/EntWebsiteTabs';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

const styles = {
  container: css`
    position: relative;
  `,
  tabsContainer: css`
    display: flex;
    overflow: auto;
    border-bottom: 1px solid #929599;
    padding: 4px 0;
    position: relative;
    justify-content: space-between;

    ::-webkit-scrollbar {
      display: none;
    }

    @media (hover: none) and (pointer: coarse) {
      -ms-overflow-style: none; /* Internet Explorer 10+ */
      scrollbar-width: none; /* Firefox */
      ::-webkit-scrollbar {
        display: none; /* Safari and Chrome */
      }
    }
  `,
  primaryTabs: (theme: Theme, showArrows = false) => css`
    flex-shrink: 0;
    display: flex;
    margin-left: ${showArrows ? '24px' : 'unset'};

    a {
      margin-right: 24px;
    }

    ${breakpoints.down('sm')} {
      margin-left: 24px;
    }
  `,
  secondaryTabs: css`
    flex-shrink: 0;
    display: flex;
    align-items: center;

    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  mobileSecondaryTabs: css`
    display: flex;
    flex-direction: column;
    padding: 16px 24px;
    border-bottom: 1px solid #929599;
    padding-left: 0;

    ${breakpoints.up('xs')} {
      padding-left: 24px;
    }

    ${breakpoints.up('md')} {
      display: none;
    }
  `,
  primaryTabLink: css`
    flex-shrink: 0;
    padding: 12px;
    font-size: 18px;
    color: var(--cds-color-neutral-primary) !important;

    :hover {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }
  `,
  secondaryLink: css`
    margin-right: 1.25em;
    font-weight: bold;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-style: normal;
    letter-spacing: -0.1px;

    > * {
      display: flex;
    }
  `,
  mobileSecondaryLink: css`
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.1px;
    padding-bottom: 12px;
    display: flex;
    align-items: center;

    > * {
      display: flex;
    }
  `,
  seeAlso: css`
    font-size: 16px;
    margin-right: 1.25em;
  `,
  mobileSeeAlso: css`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.1px;
    padding-bottom: 12px;
  `,
  tabHighlight: css`
    height: 4px;
    position: absolute;
    left: 0;
    border-radius: 2px 2px 0 0;
    background-color: var(--cds-color-neutral-primary);
    width: calc(100% - 24px);
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    position: absolute;
    bottom: -4px;
  `,
  panelStyle: css`
    ${breakpoints.down('xs')} {
      padding-left: 0;
      padding-right: 0;
    }
  `,
  blockStyle: css`
    padding-top: 6px;
    padding-bottom: 0;

    ${breakpoints.down('sm')} {
      padding: 6px 0 0;
    }
  `,
  arrowsCnt: (theme: Theme, showArrows = false) => css`
    display: ${showArrows ? 'block' : 'none'};

    @media (hover: none) and (pointer: coarse) {
      display: none;
    }
  `,
  arrow: css`
    position: absolute;
    top: 9px;
    height: calc(100% - 18px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 100;
    border: none;
    outline: none;
    box-shadow: none;
  `,
  selectionCnt: css`
    border-bottom: 1px solid #929599;
    padding-bottom: 12px;
    ${breakpoints.up('sm')} {
      display: none;
    }
  `,
  selectField: css`
    label {
      display: none;
    }

    > div {
      margin: 12px 24px;
    }
  `,
  tabsScrollableContent: css`
    display: flex;
    justify-content: space-between;
    flex-shrink: 0;
    width: 100%;
  `,
};

export type Props = BlockModuleInnerProps<EntWebsiteTabsFragment>;

export function TabsModule({ content, assembly }: Props) {
  const track = useRetracked();
  const theme = useTheme();

  const primaryTabsRef = useRef<HTMLDivElement>(null);
  const scollContentRef = useRef<HTMLDivElement>(null);

  const [showArrows, setShowArrows] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(true);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const activeTabIndex: number | undefined = useMemo(
    () => (content?.activeTabIndex ?? 1) - 1,
    [content?.activeTabIndex]
  );

  useEffect(() => {
    const listener = () => {
      if (!scollContentRef?.current || !primaryTabsRef?.current) return;

      let scrollContentWidth = 0;

      scollContentRef?.current?.childNodes?.forEach((el) => {
        scrollContentWidth += (el as HTMLDivElement)?.clientWidth;
      });

      setShowArrows(scrollContentWidth > primaryTabsRef?.current?.clientWidth);
      setShowLeftArrow(primaryTabsRef?.current?.scrollLeft !== 0);
      setShowRightArrow(primaryTabsRef?.current?.scrollLeft !== primaryTabsRef?.current?.clientWidth);
    };

    listener();
    window.addEventListener('resize', listener);

    return () => window.removeEventListener('resize', listener);
  }, []);

  const onScrollButtonPress = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((dir: 'left' | 'right') => {
    if (!primaryTabsRef?.current) return;

    const clientWidth = primaryTabsRef?.current?.clientWidth;
    const scrollTo =
      dir === 'left'
        ? Math.max(primaryTabsRef?.current?.scrollLeft - clientWidth, 0)
        : Math.min(primaryTabsRef?.current?.scrollLeft + clientWidth, clientWidth);

    primaryTabsRef.current?.scrollTo({
      left: scrollTo,
      behavior: 'smooth',
    });
    setShowLeftArrow(scrollTo !== 0);
    setShowRightArrow(scrollTo !== clientWidth);
  }, []);

  const onSelectionChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (e) => {
      const cta = content?.primaryButtons?.ctasCollection?.items?.find(
        (localItem) => localItem?.sys?.id === e?.target?.value
      );

      if (cta && cta?.ctaTarget) {
        track({ trackingName: 'primary_tab', action: 'click', trackingData: { url: cta?.ctaTarget } });
        window.open(cta?.ctaTarget, cta?.shouldOpenInNewWindow ? '_blank' : '_self');
      }
    },
    [content?.primaryButtons?.ctasCollection?.items, track]
  );

  if (!content?.primaryButtons?.ctasCollection || content?.primaryButtons?.ctasCollection?.items?.length < 2)
    return null;

  return (
    <BlockLayout
      assembly={{ ...assembly }}
      renderContent={() => (
        <div css={styles.container}>
          <div css={styles.selectionCnt}>
            <SelectField
              css={styles.selectField}
              fullWidth
              onChange={onSelectionChange}
              value={content?.primaryButtons?.ctasCollection?.items?.[activeTabIndex]?.sys?.id}
            >
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-expect-error */}
              {content?.primaryButtons?.ctasCollection?.items?.map(
                (item) =>
                  item?.ctaTarget && (
                    <SelectOption key={item?.sys?.id} value={item?.sys?.id}>
                      {item?.ctaText}
                    </SelectOption>
                  )
              )}
            </SelectField>
          </div>
          <div
            css={css`
              position: relative;

              ${breakpoints.down('xs')} {
                display: none;
              }
            `}
          >
            <div ref={primaryTabsRef} css={styles.tabsContainer}>
              <div ref={scollContentRef} css={styles.tabsScrollableContent}>
                <div css={styles.primaryTabs(theme, showArrows)} role="tablist">
                  {content?.primaryButtons?.ctasCollection?.items?.map((item, index) => (
                    <span
                      key={item?.sys?.id}
                      css={css`
                        position: relative;
                      `}
                    >
                      <Button
                        component={TrackedLink2}
                        trackingName="primary_tab"
                        role="tab"
                        aria-selected={index === activeTabIndex}
                        variant="ghost"
                        href={item?.ctaTarget ?? ''}
                        css={styles.primaryTabLink}
                      >
                        {item?.ctaText ?? ''}
                      </Button>
                      {index === activeTabIndex && <span css={styles.tabHighlight} />}
                    </span>
                  ))}
                </div>
                {content?.secondaryButtons?.ctasCollection &&
                  content?.secondaryButtons?.ctasCollection?.items?.length > 0 && (
                    <div css={styles.secondaryTabs}>
                      <Typography2 component="p" css={styles.seeAlso}>
                        {_t('See also:')}
                      </Typography2>
                      {content?.secondaryButtons?.ctasCollection?.items?.map((item) => (
                        <Link
                          component={TrackedLink2}
                          trackingName="secondary_tab"
                          key={item?.sys?.id}
                          icon={<ArrowNextIcon size="small" />}
                          iconPosition="after"
                          href={item?.ctaTarget ?? ''}
                          css={styles.secondaryLink}
                        >
                          {item?.ctaText ?? ''}
                        </Link>
                      ))}
                    </div>
                  )}
              </div>
            </div>
            <div css={styles.arrowsCnt(theme, showArrows)}>
              <button
                aria-label={_t('Tabs Scroll Left')}
                type="button"
                onClick={() => onScrollButtonPress('left')}
                css={[
                  styles.arrow,
                  css`
                    left: 0;
                    /* stylelint-disable-next-line color-function-notation */
                    background: linear-gradient(90deg, rgba(255, 255, 255, 100%) 0%, rgba(255, 255, 255, 0%) 100%);
                    display: ${showLeftArrow ? 'flex' : 'none'};
                  `,
                ]}
              >
                <ChevronPreviousIcon size="medium" strokeWidth={4} />
              </button>
              <button
                aria-label={_t('Tabs Scroll Right')}
                type="button"
                onClick={() => onScrollButtonPress('right')}
                css={[
                  styles.arrow,
                  css`
                    right: 0;
                    /* stylelint-disable-next-line color-function-notation */
                    background: linear-gradient(90deg, rgba(255, 255, 255, 0%) 0%, rgba(255, 255, 255, 100%) 100%);
                    display: ${showRightArrow ? 'flex' : 'none'};
                  `,
                ]}
              >
                <ChevronNextIcon size="medium" />
              </button>
            </div>
          </div>

          {content?.secondaryButtons?.ctasCollection &&
            content?.secondaryButtons?.ctasCollection?.items?.length > 0 && (
              <div css={styles.mobileSecondaryTabs}>
                <Typography2 component="p" css={styles.mobileSeeAlso}>
                  {_t('See also:')}
                </Typography2>
                {content?.secondaryButtons?.ctasCollection?.items?.map((item) => (
                  <Link
                    component={TrackedLink2}
                    trackingName="secondary_tab"
                    key={item?.sys?.id}
                    icon={<ArrowNextIcon />}
                    iconPosition="after"
                    css={styles.mobileSecondaryLink}
                    href={item?.ctaTarget ?? ''}
                  >
                    {item?.ctaText ?? ''}
                  </Link>
                ))}
              </div>
            )}
        </div>
      )}
      panelStyle={styles.panelStyle}
      blockStyle={styles.blockStyle}
    />
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockTabsModule = withBlockQuery<EntWebsiteTabsFragment, EntWebsiteBlockTabsQuery>(
  entWebsiteBlockTabsQuery
)(TabsModule);
